import React, { useState, useEffect } from "react";
import { FileText, Loader2, Sparkles, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/common/Markdown";
import { DetailLevel } from "@/components/common/DetailLevel";
import { workspacesApi, getAiDetail, setAiDetail } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";
import { toast } from "sonner";

const TYPES = ["student", "five_min", "one_min", "fifteen_min", "executive", "action"];

// Synthesised summary across ALL sources in the workspace (one unified study text).
export const WorkspaceSummary = ({ wsId, sourceCount }) => {
    const { t } = useTranslation();
    const [type, setType] = useState("student");
    const [detail, setDetail] = useState(getAiDetail());
    const [summaries, setSummaries] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const list = await workspacesApi.listSummaries(wsId);
                const map = {};
                (list || []).forEach((s) => { map[`${s.type}:${s.detail}`] = s.content; });
                setSummaries(map);
            } catch (e) { /* ignore */ }
        })();
    }, [wsId]);

    const key = `${type}:${detail}`;
    const content = summaries[key];

    const generate = async (refresh = false) => {
        if (!sourceCount) { toast.error(t("workspace.chat.needSource")); return; }
        setLoading(true);
        try {
            const res = await workspacesApi.summary(wsId, type, "auto", refresh, detail);
            setSummaries((m) => ({ ...m, [key]: res.content }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("workspace.summary.failed"));
        } finally { setLoading(false); }
    };

    const changeDetail = (d) => { setDetail(d); setAiDetail(d); };

    return (
        <div data-testid="workspace-summary">
            <div className="rounded-2xl border border-border bg-surface-1 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-[hsl(var(--primary))]" /><h3 className="font-sans-display text-sm font-semibold text-t1">{t("workspace.summary.title")}</h3></div>
                    <DetailLevel value={detail} onChange={changeDetail} compact />
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                    {TYPES.map((ty) => (
                        <button key={ty} data-testid={`ws-summary-type-${ty}`} onClick={() => setType(ty)}
                            className={cn("rounded-lg px-2.5 py-1.5 text-xs transition-colors", type === ty ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "bg-surface-2 text-t3 hover:text-t1")}>
                            {t(`workspace.summary.types.${ty}`)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-4">
                {content ? (
                    <div className="rounded-2xl border border-border bg-surface-1 p-6">
                        <div className="mb-3 flex justify-end">
                            <Button data-testid="ws-summary-refresh" variant="ghost" size="sm" className="gap-1.5 text-t4 hover:text-t1" disabled={loading} onClick={() => generate(true)}>
                                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} {t("workspace.summary.regenerate")}
                            </Button>
                        </div>
                        <Markdown className="font-reading leading-relaxed text-t2">{content}</Markdown>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-surface-1 px-6 py-14 text-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-2 text-[hsl(var(--primary))]"><FileText className="h-6 w-6" /></div>
                        <p className="mt-4 max-w-sm text-sm text-t3">{t("workspace.summary.empty")}</p>
                        <Button data-testid="ws-summary-generate" className="mt-4 gap-2" disabled={loading} onClick={() => generate(false)}>
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t("workspace.summary.generate")}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};
